import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';

const PLAYWRIGHT_TEST_EMAILS = ['playwright.test@wisedoc.net'];

const isAutomationRuntime =
  typeof navigator !== 'undefined' && !!navigator.webdriver;

const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const sentryEnvironment = process.env.REACT_APP_ENVIRONMENT || 'development';
const sentryTunnel = process.env.REACT_APP_SENTRY_TUNNEL;
const isProductionEnvironment = sentryEnvironment === 'production';
const enterpriseTags = {
  app: 'enterprise',
  product: 'enterprise',
  surface: 'enterprise-web',
};
const replayUnmaskSelectors = ['[data-sentry-unmask]'];
const replayMaskSelectors = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[data-sentry-mask]',
  '.sentry-mask',
];
const replayBlockSelectors = ['[data-sentry-block]', '.sentry-block'];

const applyEnterpriseTags = (event) => ({
  ...event,
  tags: {
    ...event.tags,
    ...enterpriseTags,
  },
});

const SAFE_STATIC_PATH_SEGMENT = /^[a-z][a-z0-9-]*$/i;
const ROUTE_TEMPLATE_PATH_TAIL = ':path';
const STATIC_PATH_SEGMENTS = new Set([
  'abort',
  'accept',
  'addPaymentDetails',
  'analytics',
  'assistant',
  'billing-contact',
  'billingStatus',
  'billings',
  'bootstrap',
  'bulkUpload',
  'candidateClarityQuestionsStatus',
  'candidateDecision',
  'clear-all',
  'company',
  'complete',
  'createApplication',
  'createJobPosting',
  'createWorkspace',
  'current-month-bill',
  'delete',
  'deleteJobPosting',
  'download',
  'enterprise',
  'enterpriseInvoice',
  'enterpriseInvoices',
  'enterprisePaymentActions',
  'enterpriseSetupIntent',
  'exists',
  'feedback',
  'file',
  'generateCandidateClarityQuestions',
  'getAllApplications',
  'getAllJobPostings',
  'getAllWorkspaces',
  'getApplicationDetails',
  'getApplicationsSince',
  'getInterviewDetails',
  'getJobDetails',
  'getJobPosting',
  'getCandidateReport',
  'hasSavedCard',
  'hiring-metrics',
  'image',
  'initiate',
  'interviewRecording',
  'invitation',
  'invoices',
  'job',
  'jobs',
  'joinInterviewEntry',
  'launchApplicationInterview',
  'leave',
  'me',
  'member',
  'members',
  'message',
  'notifications',
  'organizations',
  'part-url',
  'prepareInterviewEntry',
  'presigned-url',
  'price',
  'profile',
  'public',
  'read-all',
  'resend-invite',
  'resolveInterviewEntry',
  'retry',
  'role',
  'roleEvidenceOverride',
  'roleEvidenceReport',
  'roleVerificationCalibration',
  'sales-dashboard',
  'saveResumeData',
  'sendInterviewInvitations',
  'team',
  'updateApplication',
  'updateJobPosting',
  'updateMemberProfile',
  'updateWorkspace',
  'upload',
  'user',
]);
const ROUTE_TEMPLATES = [
  ['analytics', 'enterprise', ':orgId', 'hiring-metrics'],
  ['billings', ':customerId'],
  ['billingStatus', ':orgId'],
  ['candidateClarityQuestionsStatus', ':orgId', ':jobId', ':requestId'],
  ['candidateDecision', ':jobId', ':applicationId'],
  ['deleteJobPosting', ':orgId', ':jobId'],
  ['download', 'presigned-url', ROUTE_TEMPLATE_PATH_TAIL],
  ['enterpriseInvoice', ':invoiceId', 'retry'],
  ['enterpriseInvoices', ':orgId'],
  ['getAllApplications', ':orgId', ':jobId'],
  ['getAllJobPostings', ':orgId'],
  ['getAllWorkspaces', ':orgId'],
  ['getApplicationDetails', ':jobId', ':applicationId'],
  ['getApplicationsSince', ':orgId', ':jobId'],
  ['getInterviewDetails', ':applicationId'],
  ['getJobDetails', 'job', ':jobId'],
  ['getJobPosting', ':orgId', ':jobId'],
  ['hasSavedCard', ':orgId'],
  ['interviewRecording', ':applicationId', 'abort'],
  ['interviewRecording', ':applicationId', 'complete'],
  ['interviewRecording', ':applicationId', 'initiate'],
  ['interviewRecording', ':applicationId', 'part-url'],
  ['joinInterviewEntry', ':applicationId'],
  ['prepareInterviewEntry', ':applicationId'],
  ['public', 'getCandidateReport', ':candidateId'],
  ['resolveInterviewEntry', ':applicationId'],
  ['roleEvidenceOverride', ':jobId', ':applicationId'],
  ['roleEvidenceReport', ':jobId', ':applicationId'],
  ['roleVerificationCalibration', ':jobId', ':applicationId'],
  ['sales-dashboard', 'jobs', ':displayJobId'],
  ['sales-dashboard', 'organizations', ':orgId', 'jobs'],
  ['team', ':orgUuid', 'member'],
  ['team', ':orgUuid', 'members'],
  ['team', 'company', ':orgId'],
  ['team', 'members', 'leave'],
  ['team', 'members', ':memberId'],
  ['team', 'members', ':memberId', 'resend-invite'],
  ['team', 'members', ':memberId', 'role'],
  ['user', 'notifications', ':orgId'],
  ['user', 'notifications', ':orgId', 'clear-all'],
  ['user', 'notifications', ':orgId', 'delete'],
  ['user', 'notifications', ':orgId', 'read-all'],
  ['user', 'profile', ':orgId'],
];

const normalizePathSegment = (segment) => {
  if (/^\d+$/.test(segment)) {
    return ':id';
  }

  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      segment,
    )
  ) {
    return ':uuid';
  }

  if (/^[0-9a-f]{16,}$/i.test(segment)) {
    return ':id';
  }

  if (segment.length > 24 && /\d/.test(segment)) {
    return ':id';
  }

  return segment;
};

const routeTemplateMatches = (pathSegments, template) => {
  const pathTailIndex = template.indexOf(ROUTE_TEMPLATE_PATH_TAIL);

  if (pathTailIndex >= 0 && pathSegments.length < pathTailIndex + 1) {
    return false;
  }

  if (pathTailIndex < 0 && pathSegments.length !== template.length) {
    return false;
  }

  return template.every((segment, index) => {
    if (segment === ROUTE_TEMPLATE_PATH_TAIL) {
      return true;
    }

    if (segment.startsWith(':')) {
      return Boolean(pathSegments[index]);
    }

    return pathSegments[index] === segment;
  });
};

const normalizePathSegments = (pathSegments) => {
  if (!pathSegments.length) {
    return '/';
  }

  const routeTemplate = ROUTE_TEMPLATES.find((template) =>
    routeTemplateMatches(pathSegments, template),
  );

  if (routeTemplate) {
    return `/${routeTemplate.join('/')}`;
  }

  const normalizedSegments = pathSegments.map((segment) => {
    const normalizedSegment = normalizePathSegment(segment);
    if (normalizedSegment !== segment) {
      return normalizedSegment;
    }

    if (
      SAFE_STATIC_PATH_SEGMENT.test(segment) &&
      STATIC_PATH_SEGMENTS.has(segment)
    ) {
      return segment;
    }

    return ':param';
  });

  return `/${normalizedSegments.join('/')}`;
};

const getUrlBase = (baseURL) => {
  if (baseURL) {
    return baseURL;
  }

  return typeof window !== 'undefined' ? window.location.origin : undefined;
};

const parseRequestUrl = (url, baseURL) => new URL(url, getUrlBase(baseURL));

const normalizeRequestPath = (url, baseURL) => {
  if (!url) {
    return '/unknown';
  }

  try {
    const parsedUrl = parseRequestUrl(url, baseURL);
    const path = parsedUrl.pathname || '/';
    return normalizePathSegments(path.split('/').filter(Boolean));
  } catch {
    const [path] = String(url).split('?');
    return normalizePathSegments(
      (path || '/unknown').split('/').filter(Boolean),
    );
  }
};

const extractAxiosRequestDetails = (error) => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const isAxiosError = error.isAxiosError || error.name === 'AxiosError';
  const config = error.config || error.response?.config;

  if (!isAxiosError || !config) {
    return null;
  }

  const method = (config?.method || 'request').toUpperCase();
  const statusCode = error.response?.status || 'network';
  const normalizedPath = normalizeRequestPath(config?.url, config?.baseURL);
  let host = 'unknown';

  try {
    host = parseRequestUrl(config?.url || '', config?.baseURL).hostname;
  } catch {
    host = 'unknown';
  }

  return {
    method,
    statusCode: String(statusCode),
    normalizedPath,
    host,
  };
};

const applyAxiosEventDetails = (event, error) => {
  const requestDetails = extractAxiosRequestDetails(error);

  if (!requestDetails) {
    return event;
  }

  const { method, statusCode, normalizedPath, host } = requestDetails;
  const title = truncateTagValue(
    `Enterprise API ${statusCode} ${method} ${normalizedPath}`,
  );
  const message = `${method} ${normalizedPath} failed: ${
    error.message || `status ${statusCode}`
  }`;
  const exception = event.exception?.values?.[0];

  return {
    ...event,
    fingerprint: [
      'enterprise-api-request',
      host,
      method,
      statusCode,
      normalizedPath,
    ],
    tags: {
      ...event.tags,
      error_kind: 'enterprise_api_request',
      'http.method': method,
      'http.status_code': statusCode,
      'enterprise.api.host': host,
      'enterprise.api.endpoint': truncateTagValue(normalizedPath),
    },
    contexts: {
      ...event.contexts,
      enterprise_api_request: {
        method,
        status_code: statusCode,
        endpoint: normalizedPath,
        host,
      },
    },
    exception: exception
      ? {
          ...event.exception,
          values: [
            {
              ...exception,
              type: title,
              value: message,
            },
            ...event.exception.values.slice(1),
          ],
        }
      : event.exception,
  };
};

const setEnterpriseScopeTags = (tags = {}) => {
  Sentry.configureScope((scope) => {
    Object.entries({ ...enterpriseTags, ...tags }).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
  });
};

const truncateTagValue = (value) => String(value).slice(0, 200);

const parseSampleRate = (value, fallback) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }
  return Math.min(Math.max(parsedValue, 0), 1);
};

let sentryDisabled = isAutomationRuntime;

const shouldSkipLogging = () => sentryDisabled;

const shouldDisableForEmail = (email) => {
  if (!email) return false;
  return PLAYWRIGHT_TEST_EMAILS.includes(email.toLowerCase());
};

const disableSentry = () => {
  if (sentryDisabled) return;
  sentryDisabled = true;
  if (Sentry.getCurrentHub().getClient()) {
    Sentry.close();
  }
};

// todo: remove this after detaching cra from gatsby completely - navbar
let history;
const store = {};
const localStorage =
  typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: (key) => store[key] || null };
if (typeof window !== 'undefined') {
  history = createBrowserHistory();
}

const getReplayUnsupportedRequirements = () => {
  const requirements = [
    ['window', () => typeof window !== 'undefined'],
    ['document', () => typeof document !== 'undefined'],
    ['globalThis', () => typeof globalThis !== 'undefined'],
    ['fetch', () => typeof fetch === 'function'],
    [
      'MutationObserver',
      () =>
        typeof window !== 'undefined' &&
        typeof window.MutationObserver === 'function',
    ],
    ['Promise', () => typeof Promise === 'function'],
    ['URL', () => typeof URL === 'function'],
    ['TextEncoder', () => typeof TextEncoder === 'function'],
  ];

  return requirements
    .filter(([, isSupported]) => !isSupported())
    .map(([name]) => name);
};

const createReplaySetup = () => {
  const unsupportedRequirements = getReplayUnsupportedRequirements();

  if (unsupportedRequirements.length > 0) {
    return {
      integration: null,
      status: 'unsupported_browser',
      reason: unsupportedRequirements.join(','),
    };
  }

  try {
    return {
      integration: Sentry.replayIntegration({
        // @sentry/react@7.x reads sessionSampleRate/errorSampleRate from
        // the integration's own options, NOT from Sentry.init's
        // replaysSessionSampleRate/replaysOnErrorSampleRate. Pass them
        // here too so the integration actually auto-starts; otherwise
        // it just installs listeners and never records (replayEnabled
        // stays false forever, no session is created, no replay
        // envelopes are sent). v8+ would read from init options.
        sessionSampleRate: parseSampleRate(
          process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
          0,
        ),
        errorSampleRate: parseSampleRate(
          process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
          1.0,
        ),
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        unmask: replayUnmaskSelectors,
        mask: replayMaskSelectors,
        block: replayBlockSelectors,
      }),
      status: 'enabled',
      reason: 'supported',
    };
  } catch (error) {
    return {
      integration: null,
      status: 'init_failed',
      reason: error?.message || 'unknown',
      error,
    };
  }
};

const createSentryIntegrations = (replayIntegration) => {
  const integrations = [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
      tracingOrigins: [
        'localhost', // Include localhost for local development
        /https:\/\/([a-zA-Z0-9]+).execute-api.[a-z0-9-]+.amazonaws.com\/.*/, // Match all AWS API Gateway URLs
        /https:\/\/apistage.wisedoc.net\/.*/,
        /https:\/\/api.wisedoc.net\/.*/,
        /https:\/\/apitetra.wisedoc.net\/.*/,
      ],
    }),
  ];

  if (replayIntegration) {
    integrations.push(replayIntegration);
  }

  integrations.push(
    Sentry.feedbackIntegration({
      // App.jsx owns when the actor is shown (hidden on board surfaces, shown
      // elsewhere), so opt out of Sentry auto-injecting its own floating widget.
      autoInject: false,
      colorScheme: 'system',
      showBranding: false,

      useSentryUser: {
        email: 'email',
        name: 'name',
      },
    }),
    new Sentry.BrowserProfilingIntegration(),
  );

  return integrations;
};

const captureReplaySetupStatus = ({ status, reason, error }) => {
  if (status === 'enabled') {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('sentry_replay_status', status);
    scope.setTag('sentry_replay_status_reason', truncateTagValue(reason));

    if (error) {
      Sentry.captureException(error);
      return;
    }

    Sentry.captureMessage('Enterprise Sentry Replay disabled for this session');
  });
};

const createSentryInitOptions = (replaySetup) => ({
  dsn: sentryDsn,
  environment: sentryEnvironment,
  release: process.env.REACT_APP_SENTRY_RELEASE,
  tunnel: sentryTunnel || undefined,
  replaysSessionSampleRate: replaySetup.integration
    ? parseSampleRate(
        process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
        0,
      )
    : 0,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: replaySetup.integration
    ? parseSampleRate(
        process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
        1.0,
      )
    : 0,
  profilesSampleRate: parseSampleRate(
    process.env.REACT_APP_SENTRY_PROFILES_SAMPLE_RATE,
    isProductionEnvironment ? 0 : 1.0,
  ),
  tracesSampleRate: parseSampleRate(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
    isProductionEnvironment ? 0.1 : 1.0,
  ),
  integrations: createSentryIntegrations(replaySetup.integration),
  beforeSend(event, hint) {
    if (shouldSkipLogging()) {
      return null;
    }
    return applyAxiosEventDetails(
      applyEnterpriseTags(event),
      hint?.originalException,
    );
  },
  beforeSendTransaction(event) {
    if (shouldSkipLogging()) {
      return null;
    }
    return applyEnterpriseTags(event);
  },
});

const Init = () => {
  if (shouldSkipLogging() || !sentryDsn) {
    return;
  }

  const replaySetup = createReplaySetup();

  try {
    Sentry.init(createSentryInitOptions(replaySetup));
    setEnterpriseScopeTags({
      sentry_replay_status: replaySetup.status,
      sentry_replay_status_reason: truncateTagValue(replaySetup.reason),
    });
    captureReplaySetupStatus(replaySetup);
  } catch (error) {
    const fallbackReplaySetup = {
      integration: null,
      status: 'init_failed',
      reason: error?.message || 'unknown',
      error,
    };

    try {
      Sentry.init(createSentryInitOptions(fallbackReplaySetup));
      setEnterpriseScopeTags({
        sentry_replay_status: fallbackReplaySetup.status,
        sentry_replay_status_reason: truncateTagValue(
          fallbackReplaySetup.reason,
        ),
      });
      captureReplaySetupStatus(fallbackReplaySetup);
    } catch {
      sentryDisabled = true;
    }
  }
};

const LogUncaughtError = (error, errorInfo) => {
  if (shouldSkipLogging()) return;
  Sentry.withScope((scope) => {
    Object.keys(errorInfo).forEach((key) => {
      scope.setExtra(key, errorInfo[key]);
    });
    Sentry.setTag(
      'Redirect url ',
      localStorage.getItem('rurl') || 'No Redirect url present',
    );
    Sentry.setTag(
      'Service provider',
      localStorage.getItem('sp') || 'No Sp present',
    );
    Sentry.setTag(
      'Signin method',
      localStorage.getItem('sign-in-method') || 'No signin method present',
    );
    if (navigator.connection) {
      Sentry.setTag('Network Type', navigator.connection.effectiveType);
    }
    Sentry.captureException(error);
  });
};

const LogError = (error, errorInfo = {}) => {
  if (shouldSkipLogging()) return;
  // if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
  //   console.error('SENTRY LOG', error);
  // }
  Sentry.withScope((scope) => {
    Object.keys(errorInfo).forEach((key) => {
      scope.setExtra(key, errorInfo[key]);
    });
    Sentry.setTag(
      'Redirect url ',
      localStorage.getItem('rurl') || 'No Redirect url present',
    );
    Sentry.setTag(
      'Service provider',
      localStorage.getItem('sp') || 'No Sp present',
    );
    Sentry.setTag(
      'Signin method',
      localStorage.getItem('sign-in-method') || 'No signin method present',
    );
    if (navigator.connection) {
      Sentry.setTag('Network Type', navigator.connection.effectiveType);
    }
    Sentry.captureException(error);
  });
};

const Log = (message) => {
  if (shouldSkipLogging()) return;
  Sentry.setTag(
    'Redirect url ',
    localStorage.getItem('rurl') || 'No Redirect url present',
  );
  Sentry.setTag(
    'Service provider',
    localStorage.getItem('sp') || 'No Sp present',
  );
  Sentry.setTag(
    'Signin method',
    localStorage.getItem('sign-in-method') || 'No signin method present',
  );
  if (navigator.connection) {
    Sentry.setTag('Network Type', navigator.connection.effectiveType);
  }
  Sentry.captureMessage(message);
};

const normalizeUserField = (value) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

const SetUserLogScope = (id, name, email) => {
  const normalizedEmail = normalizeUserField(
    email || (typeof id === 'string' && id.includes('@') ? id : undefined),
  )?.toLowerCase();
  if (shouldDisableForEmail(normalizedEmail)) {
    disableSentry();
    return;
  }
  if (shouldSkipLogging()) return;

  const normalizedId = normalizeUserField(id) || normalizedEmail;
  const normalizedName = normalizeUserField(name);

  Sentry.configureScope((scope) => {
    scope.setUser({
      id: normalizedId,
      name: normalizedName,
      email: normalizedEmail,
    });
  });
};

const ClearUserLogScope = () => {
  if (shouldSkipLogging()) return;
  Sentry.configureScope((scope) => {
    scope.setUser(null);
  });
};

export default {
  Init,
  LogUncaughtError,
  Log,
  LogError,
  SetUserLogScope,
  ClearUserLogScope,
};
